/**
 * BuildingLoader — editorial loading state for the Executive Report.
 *
 * The report's backend generation (Azure OpenAI narrative + score aggregation)
 * is synchronous and takes ~10-30s. There's no SSE/WebSocket progress feed,
 * so we frame the wait as a multi-stage Guardian-voice walkthrough rather
 * than a generic spinner. The stages are honest about what Guardian *would*
 * be doing — gathering scores, reading 90 days of compliance signals,
 * drafting the narrative, polishing — without claiming a real progress
 * percentage we don't have.
 *
 * Visual language:
 *   - kicker pill ("Building · By Guardian") matching the standfirst kicker
 *   - italic-mixed headline ("Guardian is *drafting* your brief.")
 *   - vertical stage list with an animated indicator on the active stage
 *   - 4px double navy bottom rule, mirroring the standfirst-rule editorial bracket
 *
 * Stage progression is timer-driven (frontend-only). Each stage advances on
 * its own cadence; the final stage stays "active" until the data resolves
 * (the page swaps the loader out at that point — we don't auto-complete).
 */

import { useEffect, useState } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

interface Stage {
  /** Stable id for React keying. */
  id: string
  /** Italic Guardian-voice copy. Renders in italic mixed-case to match the
   *  editorial template; no period at the end (the dropcap state replaces it). */
  label: string
  /** Sub-line shown under the active stage in slate-400 — short prose, plain. */
  detail: string
  /** Milliseconds before the loader advances past this stage. The final
   *  stage is "sticky" (its dwell is irrelevant — the page replaces the
   *  loader once `isLoading` flips). */
  dwellMs: number
}

const STAGES: ReadonlyArray<Stage> = [
  {
    id: 'gather',
    label: 'Guardian is gathering your scores',
    detail: 'Pulling Guardian Score, Secure Score, BPA, MFA, and baseline pillars together.',
    dwellMs: 2400,
  },
  {
    id: 'review',
    label: 'Reviewing the compliance signals',
    detail: 'Reading the period of drift, framework controls, and operational activity.',
    dwellMs: 3200,
  },
  {
    id: 'draft',
    label: 'Drafting the executive brief',
    detail: 'Composing the board-ready narrative from the period’s data.',
    dwellMs: 5200,
  },
  {
    id: 'polish',
    label: 'Final polish',
    detail: 'Tightening the verdicts, double-checking the framework roll.',
    // Sticky — the page swaps the loader out when the report query resolves.
    // We pin a long dwell here (effectively "stay put") so the indicator
    // doesn't visually march past the last stage.
    dwellMs: 60_000,
  },
]

interface Props {
  /** Optional period in days — only shown in the kicker for editorial polish. */
  periodDays?: number
}

export default function BuildingLoader({ periodDays }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    // Walk through STAGES on a chained timer. Each stage advances after
    // its `dwellMs`; the last stage's timer never fires in practice because
    // the page unmounts the loader once data arrives.
    if (activeIndex >= STAGES.length - 1) return
    const stage = STAGES[activeIndex]
    const t = window.setTimeout(() => {
      setActiveIndex(i => Math.min(i + 1, STAGES.length - 1))
    }, stage.dwellMs)
    return () => window.clearTimeout(t)
  }, [activeIndex])

  const periodSegment = typeof periodDays === 'number' ? `· ${periodDays} days` : ''

  return (
    <section
      aria-live="polite"
      aria-busy="true"
      className="standfirst-rule grid grid-cols-1 gap-10 pb-12 pt-2 lg:grid-cols-[1.5fr_1fr] lg:gap-14"
    >
      <div className="lg:pr-6">
        <span
          className="
            inline-flex items-center gap-2.5 rounded-[2px] bg-[#212938] px-3.5 py-1.5
            text-[10px] font-bold uppercase tracking-[0.28em] text-white
          "
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full animate-pulse"
            style={{
              background: 'rgb(var(--color-primary-500))',
              boxShadow: '0 0 6px rgb(var(--color-primary-500))',
            }}
          />
          Building Brief · By Guardian {periodSegment}
        </span>

        <h1
          className="mt-5 font-display font-extrabold tracking-[-0.025em]"
          style={{
            fontSize: 'clamp(36px, 5vw, 60px)',
            lineHeight: 0.96,
            color: 'rgb(var(--color-text-primary))',
          }}
        >
          Guardian is{' '}
          <em
            style={{
              fontStyle: 'italic',
              fontWeight: 600,
              color: 'rgb(var(--color-primary-700))',
            }}
          >
            drafting
          </em>
          .
          <br />
          Your brief is on the way.
        </h1>

        <p
          className="mt-5 max-w-xl text-[16px] leading-[1.55]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          Reading the period in full. The board-ready narrative pulls a
          live Azure OpenAI call, so this can take ten to thirty seconds.
        </p>

        <ol className="mt-8 space-y-4">
          {STAGES.map((stage, idx) => {
            const state = idx < activeIndex ? 'done' : idx === activeIndex ? 'active' : 'queued'
            return <StageRow key={stage.id} stage={stage} state={state} />
          })}
        </ol>
      </div>

      {/* Right column — a quiet placeholder where the ScoreBox will land.
          We mirror the dark-navy panel silhouette so the layout doesn't jump
          when the real ScoreBox replaces it. */}
      <div
        data-score-box
        className="
          relative isolate flex flex-col justify-between overflow-hidden
          rounded-md bg-dark-800 p-8 text-slate-100 lg:p-9
        "
        style={{ minHeight: 360 }}
        aria-hidden
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full opacity-[0.18]"
          style={{
            background:
              'radial-gradient(circle, rgb(var(--color-primary-500)) 0%, transparent 65%)',
          }}
        />
        <div className="relative">
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-primary-500">
            Guardian Score
          </div>
          <div className="mt-3 font-display font-extrabold leading-[0.85] tracking-[-0.045em] text-white/70">
            <span className="text-[96px] xl:text-[110px]">—</span>
            <span className="ml-1 text-[20px] xl:text-[22px] font-semibold text-white/30">/100</span>
          </div>
          <div className="mt-3 italic text-[14px] leading-[1.5] text-white/55">
            &ldquo;Compiling the brief…&rdquo;
          </div>
        </div>

        <div className="relative mt-6 border-t border-white/10 pt-5">
          <div className="mb-3 text-[9px] font-bold uppercase tracking-[0.24em] text-white/35">
            Composite
          </div>
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="h-[5px] overflow-hidden rounded-full bg-white/[0.06]"
              >
                <i
                  className="block h-full rounded-full bg-primary-500/40"
                  style={{
                    width: `${20 + i * 12}%`,
                    animation: `building-bar 2.4s ease-in-out ${i * 200}ms infinite`,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inline keyframes — kept local so we don't pollute the global
          stylesheet for a one-off building-bar animation. */}
      <style>{`
        @keyframes building-bar {
          0%, 100% { transform: scaleX(0.4); transform-origin: left; opacity: 0.4; }
          50%      { transform: scaleX(1);   transform-origin: left; opacity: 0.85; }
        }
      `}</style>
    </section>
  )
}

interface StageRowProps {
  stage: Stage
  state: 'done' | 'active' | 'queued'
}

function StageRow({ stage, state }: StageRowProps) {
  const isActive = state === 'active'
  const isDone = state === 'done'

  // Active state gets a soft Guardian-green halo; done gets a solid check;
  // queued stays muted so the eye finds the active stage first.
  return (
    <li className="flex items-start gap-4">
      <span
        aria-hidden
        className="relative mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center"
      >
        {isDone ? (
          <CheckCircleIcon
            className="h-5 w-5"
            style={{ color: 'rgb(var(--color-primary-500))' }}
          />
        ) : isActive ? (
          <>
            <span
              className="absolute h-5 w-5 rounded-full opacity-30 animate-ping"
              style={{ background: 'rgb(var(--color-primary-500))' }}
            />
            <span
              className="relative h-2.5 w-2.5 rounded-full"
              style={{
                background: 'rgb(var(--color-primary-500))',
                boxShadow: '0 0 8px rgb(var(--color-primary-500))',
              }}
            />
          </>
        ) : (
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: 'rgb(var(--color-dark-600))' }}
          />
        )}
      </span>

      <div className="flex flex-1 flex-col">
        <span
          className="text-[15px] font-semibold leading-tight"
          style={{
            color: isActive
              ? 'rgb(var(--color-text-primary))'
              : isDone
                ? 'rgb(var(--color-text-secondary))'
                : 'rgb(var(--color-dark-500))',
            fontStyle: isActive ? 'italic' : 'normal',
          }}
        >
          {stage.label}
        </span>
        {isActive && (
          <span
            className="mt-1 text-[13px] leading-snug"
            style={{ color: 'rgb(var(--color-text-secondary))' }}
          >
            {stage.detail}
          </span>
        )}
      </div>
    </li>
  )
}
